/**
 * PURE. The creatures §A12.1 DNA layout (v1, 448 bits, MSB-first, fields in exactly this order) and the §A6 slider
 * canon ranges. This table is the single source of the bit layout: the codec, the randomizer, `resolveSpec()` and the
 * debug API (`listFields()`) all iterate it.
 *
 * Slider mapping (§A6): a quantized `q ∈ [0, 2^b − 1]` maps piecewise-linearly through the neutral value so that
 * `q = 2^(b−1)` is exactly the canon default:
 * `v = q < mid ? lerp(min, neutral, q / mid) : lerp(neutral, max, (q − mid) / (2^b − 1 − mid))`.
 */

/** How a field's integer is interpreted. */
export type FieldKind = 'header' | 'enum' | 'slider' | 'palette' | 'dye' | 'tincture' | 'bool' | 'u16' | 'crc';

/** One §A12.1 field. */
export interface FieldDef {
  readonly name: string;
  readonly bits: number;
  readonly kind: FieldKind;
  /** §A12.1 group (debug listing, «Наугад» per-section scope). */
  readonly group: FieldGroup;
  /** Sliders: canon [min, neutral, max] in the field's unit (H, ×, degrees…). */
  readonly range?: readonly [number, number, number];
  /** Enum fields: the content slot that lists the options (`src/content/creature/parts.ts`). */
  readonly slot?: string;
}

export type FieldGroup = 'header' | 'head' | 'eyes' | 'face' | 'body' | 'coat' | 'garb' | 'identity' | 'heraldry' | 'soul' | 'crc';

const F = (name: string, bits: number, kind: FieldKind, group: FieldGroup, extra: { range?: readonly [number, number, number]; slot?: string } = {}): FieldDef =>
  ({ name, bits, kind, group, ...extra });
const S = (name: string, bits: number, group: FieldGroup, min: number, neutral: number, max: number): FieldDef =>
  F(name, bits, 'slider', group, { range: [min, neutral, max] });
const E = (name: string, bits: number, group: FieldGroup, slot = name): FieldDef => F(name, bits, 'enum', group, { slot });

/** The §A12.1 table, in bit order. Sum of bits = 448 (asserted in tests/creature/dna.test.ts). */
export const DNA_FIELDS: readonly FieldDef[] = [
  // Header & species (0–22)
  F('ver', 5, 'header', 'header'),
  E('bodySpecies', 5, 'header', 'species'),
  E('headSpecies', 5, 'header', 'species'),
  E('look', 3, 'header', 'look'),
  S('height', 5, 'header', 0.88, 1.0, 1.12),
  // Head shape, ears, horns, hair (23–75)
  E('skull', 4, 'head'),
  S('headH', 4, 'head', 0.26, 0.31, 0.38),
  S('headW', 4, 'head', 0.20, 0.27, 0.34),
  S('cheekFluff', 3, 'head', 0, 0.3, 1),
  S('jowls', 3, 'head', 0, 0, 1),
  S('neckLen', 3, 'head', 0.02, 0.05, 0.12),
  E('ears', 4, 'head'),
  S('earSize', 4, 'head', 0.05, 0.10, 0.16),
  S('earSet', 3, 'head', 0.5, 0.75, 1.0),
  S('earTilt', 3, 'head', -30, 0, 30),
  S('earAsym', 3, 'head', 0, 0, 70),
  E('horns', 3, 'head'),
  S('hornSize', 3, 'head', 0.5, 1.0, 1.6),
  E('hair', 4, 'head'),
  F('hairColor', 5, 'palette', 'head'),
  // Eyes & brows (76–118)
  E('eyes', 4, 'eyes'),
  E('pupil', 3, 'eyes'),
  S('eyeSize', 4, 'eyes', 0.12, 0.17, 0.24),
  S('eyeSpacing', 4, 'eyes', 0.30, 0.38, 0.50),
  S('eyeHeight', 3, 'eyes', 0.40, 0.48, 0.56),
  S('eyeTilt', 3, 'eyes', -15, 0, 15),
  S('pupilSize', 3, 'eyes', 0.25, 0.45, 0.60),
  E('iris', 3, 'eyes'),
  F('hetero', 1, 'bool', 'eyes'),
  E('iris2', 3, 'eyes', 'iris'),
  F('catchlight', 1, 'bool', 'eyes'),
  E('brows', 4, 'eyes'),
  S('browThick', 2, 'eyes', 0.6, 1, 1.6),
  S('browHeight', 2, 'eyes', -1, 0, 1),
  S('browAngle', 3, 'eyes', -20, 0, 20),
  // Snout, mouth, whiskers, beard, expression, headwear (119–180)
  E('snout', 4, 'face'),
  S('muzzle', 4, 'face', 0.0, 0.03, 0.10),
  S('snoutW', 3, 'face', 0.7, 1, 1.4),
  E('nose', 3, 'face'),
  S('noseSize', 3, 'face', 0.6, 1, 1.8),
  E('mouth', 3, 'face'),
  S('mouthW', 3, 'face', 0.6, 1, 1.5),
  E('teeth', 4, 'face'),
  S('underbite', 3, 'face', -1, 0, 1),
  E('whiskers', 3, 'face'),
  S('whiskerLen', 3, 'face', 0.5, 1, 1.8),
  S('whiskerCount', 2, 'face', 1, 2, 4),
  E('beard', 4, 'face'),
  E('mustache', 3, 'face'),
  S('beardLen', 3, 'face', 0.5, 1, 1.8),
  E('expr', 4, 'face'),
  S('exprInt', 3, 'face', 0, 0.7, 1),
  S('exprAsym', 2, 'face', 0, 0, 1),
  E('headwear', 5, 'face'),
  // Body (181–268)
  E('torso', 3, 'body'),
  S('torsoLen', 4, 'body', 0.30, 0.36, 0.42),
  S('chestW', 4, 'body', 0.75, 1, 1.35),
  S('shoulderW', 4, 'body', 0.7, 1, 1.4),
  S('hipW', 3, 'body', 0.8, 1, 1.3),
  E('belly', 3, 'body'),
  S('bellySize', 3, 'body', 0, 0.3, 1),
  E('posture', 3, 'body'),
  S('hunch', 3, 'body', -1, 0, 1),
  S('sway', 3, 'body', 0, 0.6, 1),
  S('headFwd', 3, 'body', 0, 0, 1),
  E('arms', 3, 'body'),
  S('armLen', 4, 'body', 0.32, 0.40, 0.48),
  S('armThick', 3, 'body', 0.6, 1, 1.5),
  E('hands', 3, 'body'),
  E('digits', 2, 'body'),
  S('handSize', 3, 'body', 0.7, 1, 1.5),
  E('legType', 1, 'body'),
  E('legs', 3, 'body'),
  S('legLen', 4, 'body', 0.26, 0.33, 0.40),
  S('legThick', 3, 'body', 0.6, 1, 1.5),
  S('stanceW', 3, 'body', 0.8, 1, 1.4),
  E('feet', 3, 'body'),
  S('footSize', 3, 'body', 0.08, 0.11, 0.16),
  E('tail', 4, 'body'),
  S('tailLen', 4, 'body', 0.8, 1.15, 1.5),
  S('tailThick', 3, 'body', 0.018, 0.025, 0.04),
  S('tailCurl', 3, 'body', -1, 0, 1),
  // Coat (269–313)
  F('furBase', 5, 'palette', 'coat'),
  F('furStripe', 5, 'palette', 'coat'),
  F('furBelly', 5, 'palette', 'coat'),
  E('flesh', 2, 'coat'),
  E('pattern', 4, 'coat'),
  S('patScale', 3, 'coat', 0.5, 1, 2),
  S('patContrast', 3, 'coat', 0.2, 0.7, 1),
  S('whiteAmt', 3, 'coat', 0, 0, 1),
  E('mark1', 4, 'coat', 'mark'),
  E('mark2', 4, 'coat', 'mark'),
  S('outline', 2, 'coat', 0.8, 1, 1.4),
  S('furStroke', 2, 'coat', -0.2, 0, 0.2),
  S('patina', 3, 'coat', 0, 0.15, 1),
  // Garb (314–353)
  E('garment', 5, 'garb'),
  E('cape', 3, 'garb'),
  E('wings', 3, 'garb'),
  E('footwear', 3, 'garb'),
  E('belt1', 4, 'garb', 'belt'),
  E('belt2', 4, 'garb', 'belt'),
  E('neckAcc', 4, 'garb'),
  E('idleProp', 4, 'garb'),
  F('dye0', 4, 'dye', 'garb'),
  F('dye1', 4, 'dye', 'garb'),
  E('metal', 2, 'garb'),
  // Identity (354–384)
  E('humour', 3, 'identity'),
  E('voice', 3, 'identity'),
  S('voicePitch', 3, 'identity', 0.7, 1.0, 1.4),
  F('nameMode', 1, 'bool', 'identity'),
  F('pronoun', 1, 'bool', 'identity'),
  E('title', 5, 'identity'),
  E('name', 8, 'identity'),
  E('epithet', 7, 'identity'),
  // Heraldry (385–421)
  E('shield', 3, 'heraldry'),
  E('division', 4, 'heraldry'),
  F('tinctA', 3, 'tincture', 'heraldry'),
  F('tinctB', 3, 'tincture', 'heraldry'),
  E('ordinary', 4, 'heraldry'),
  F('ordTinct', 3, 'tincture', 'heraldry'),
  E('charge', 5, 'heraldry'),
  E('chargeCount', 2, 'heraldry'),
  F('chargeTinct', 3, 'tincture', 'heraldry'),
  E('motto', 6, 'heraldry'),
  F('ruleBreak', 1, 'bool', 'heraldry'),
  // Soul seed (422–437) and checksum (438–447)
  F('soulSeed', 16, 'u16', 'soul'),
  F('crc', 10, 'crc', 'crc'),
];

/** Total bits (448). */
export const DNA_BITS = DNA_FIELDS.reduce((s, f) => s + f.bits, 0);

/** Field lookup by name. */
export const FIELD_BY_NAME: ReadonlyMap<string, FieldDef> = new Map(DNA_FIELDS.map((f) => [f.name, f]));

/** Bit offset of each field (MSB-first order). */
export const FIELD_OFFSET: ReadonlyMap<string, number> = (() => {
  const m = new Map<string, number>();
  let o = 0;
  for (const f of DNA_FIELDS) { m.set(f.name, o); o += f.bits; }
  return m;
})();

/** Fields carried by `CreatureDNA.fields` (every field but the ones promoted to top-level members, §3.6). */
export const PROMOTED_FIELDS = new Set(['ver', 'bodySpecies', 'headSpecies', 'pronoun', 'soulSeed', 'crc']);

/** Max integer value of a field. */
export const fieldMax = (f: FieldDef): number => (2 ** f.bits) - 1;

/** The "neutral" quantized value of a slider (`2^(b−1)`). */
export const sliderMid = (f: FieldDef): number => 2 ** (f.bits - 1);

/** Slider q → real value (creatures §A6 piecewise-linear mapping through the neutral value). */
export function sliderValue(f: FieldDef, q: number): number {
  const [min, neutral, max] = f.range!;
  const mid = sliderMid(f);
  const top = fieldMax(f);
  const qq = Math.max(0, Math.min(top, Math.round(q)));
  return qq < mid ? min + (neutral - min) * (qq / mid) : neutral + (max - neutral) * ((qq - mid) / (top - mid));
}

/** Real value → nearest slider q (inverse of {@link sliderValue}; used by species defaults, presets and fixtures). */
export function sliderQ(f: FieldDef, value: number): number {
  // Ties (a flat segment, e.g. jowls 0/0/1) resolve to the q nearest the neutral `2^(b−1)`.
  const mid = sliderMid(f);
  let best = 0;
  let bestErr = Infinity;
  for (let q = 0; q <= fieldMax(f); q++) {
    const e = Math.abs(sliderValue(f, q) - value);
    if (e < bestErr - 1e-12 || (Math.abs(e - bestErr) <= 1e-12 && Math.abs(q - mid) < Math.abs(best - mid))) { bestErr = Math.min(e, bestErr); best = q; }
  }
  return best;
}

/** Every slider field name. */
export const SLIDER_FIELDS: readonly string[] = DNA_FIELDS.filter((f) => f.kind === 'slider').map((f) => f.name);
